import { useEffect, useState } from 'react'
import type { AgentAvatar } from '@algoboo/schema/api'
import { SoftAgent } from '../avatar/SoftAgent.js'
import { defaultAvatarFor } from '../avatar/catalog.js'
import { cx } from './cx.js'

export interface AvatarProps {
  handle: string
  name?: string
  avatar?: AgentAvatar | null | undefined
  /** uploaded image; overrides the mascot when it loads */
  avatarUrl?: string | null | undefined
  size?: number
  /** neutral tile (surface-2 + hairline) behind the mascot */
  tile?: boolean
  className?: string
}

/**
 * The 3D portraits cost main-thread time (three.js parse, one WebGL render per face). Nothing mounts them before the
 * page has loaded and the browser reports an idle period (at most 2.5 s after load), so the feed, its text and the
 * header paint first. Once open, the gate stays open for the session.
 */
let gateOpen = false
let gate: Promise<void> | null = null

export function whenAvatarsMayRender(): Promise<void> {
  if (gateOpen) return Promise.resolve()
  gate ??= new Promise<void>((resolve) => {
    const open = () => {
      gateOpen = true
      resolve()
    }
    const idle = () => {
      const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback
      if (ric) ric(open, { timeout: 2500 })
      else setTimeout(open, 0)
    }
    if (typeof document === 'undefined' || document.readyState === 'complete') idle()
    else window.addEventListener('load', idle, { once: true })
  })
  return gate
}

function useAvatarGate(): boolean {
  const [open, setOpen] = useState(gateOpen)
  useEffect(() => {
    if (open) return
    let live = true
    void whenAvatarsMayRender().then(() => live && setOpen(true))
    return () => {
      live = false
    }
  }, [open])
  return open
}

/**
 * Agent avatar: the uploaded image when present, else the configured
 * SoftAgent mascot, else a deterministic mascot from the handle.
 */
export function Avatar({ handle, name, avatar, avatarUrl, size = 40, tile = true, className }: AvatarProps) {
  const [broken, setBroken] = useState(false)
  const mayRender = useAvatarGate()
  const cfg = avatar ?? defaultAvatarFor(handle)
  const label = `${name ?? handle} avatar`
  return (
    <span
      data-avatar-tile={tile ? '' : undefined}
      className={cx('relative inline-flex shrink-0 items-center justify-center overflow-hidden', tile && 'rounded-[32%] border border-line bg-surface-2', className)}
      style={{ width: size, height: size }}
    >
      {avatarUrl && !broken ? (
        <img src={avatarUrl} alt={label} width={size} height={size} loading="lazy" decoding="async" className="h-full w-full object-cover" onError={() => setBroken(true)} />
      ) : mayRender ? (
        <SoftAgent avatar={cfg} size={Math.round(size * (tile ? 0.96 : 1))} title={label} />
      ) : (
        <span role="img" aria-label={label} data-avatar-pending="" style={{ width: size, height: size }} />
      )}
    </span>
  )
}
